import { ChangeEvent, FC, useRef, useState } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";

import { firestore } from "../../../firebase/config";
import {
  ButtonColumnContainer,
  RedP,
  SpreadApartArea,
} from "../../../global.styles";
import Button from "../../UI/button/button.component";
import FormInput from "../../UI/form-input/form-input.component";
import { StyledTextArea } from "../../UI/textarea/textarea.component";
import useAutosizeTextArea from "../../UI/textarea/useAutosizedTextArea";
import Checkbox from "../../UI/checkbox/checkbox.component";

//props types
type UgisPartnerFormProps = {
  userId: string;
  savedBusinessName: string;
  savedCommission: string;
  savedDiscount: string;
  savedNotes: string;
  savedActiveStatus: boolean;
  onFinished: (finished: boolean) => void;
};

const UgisPartnerForm: FC<UgisPartnerFormProps> = ({
  userId,
  savedBusinessName,
  savedCommission,
  savedDiscount,
  savedNotes,
  savedActiveStatus,
  onFinished,
}) => {
  const [businessName, setBusinessName] = useState(savedBusinessName);
  const [commission, setCommission] = useState(savedCommission || "0");
  const [discount, setDiscount] = useState(savedDiscount || "0");
  const [notes, setNotes] = useState(savedNotes);
  const [active, setActive] = useState(savedActiveStatus);
  const [submitError, setSubmitError] = useState("");

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef.current, notes);

  const submitHandler = async () => {
    if (!businessName.trim()) {
      setSubmitError("Business name is required");
      return;
    }
    if (
      isNaN(Number(commission.trim())) ||
      Number(commission.trim()) < 0 ||
      Number(commission.trim()) > 100
    ) {
      setSubmitError("Invalid commission percentage");
      return;
    }
    if (
      isNaN(Number(discount.trim())) ||
      Number(discount.trim()) < -100 ||
      Number(discount.trim()) > 100
    ) {
      setSubmitError("Invalid discount");
      return;
    }

    try {
      // update the partner data
      const userDocRef = doc(firestore, "users", userId);
      await updateDoc(userDocRef, {
        ugisPartnerDetails: {
          businessName: businessName,
          commissionPercentage: commission ? commission.trim() : "0", // Store commission as a string
          discount: discount ? discount.trim() : "0", // Store discount as a string
          notes: notes || "",
          active: active,
        },
        lastUpdate: serverTimestamp(),
      });
      setSubmitError("");
      onFinished(false);
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError(
          "Error changing busyness account details. Please contact support."
        );
      }
    }
  };

  return (
    <>
      {submitError && <RedP>{submitError}</RedP>}
      <FormInput
        id="busynessName"
        type="text"
        label="Enter Business Name"
        value={businessName}
        onChange={(e) => {
          setBusinessName(e.target.value);
        }}
      />
      <FormInput
        label="Drivers Comirssion %"
        id="commission"
        type="text"
        onChange={(e) => setCommission(e.target.value)}
        value={commission}
        inputMode="numeric"
      />
      <FormInput
        label="Discount %"
        id="discount"
        type="text"
        onChange={(e) => setDiscount(e.target.value)}
        value={discount}
        inputMode="numeric"
      />
      <StyledTextArea
        id="notes"
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          setNotes(e.target.value)
        }
        placeholder="Any notes or description..."
        ref={textAreaRef}
        rows={1}
        value={notes}
      />
      <ButtonColumnContainer>
        <Checkbox
          label="Partner Active"
          id="active"
          name="active"
          checked={active}
          onChange={() => setActive(!active)}
        />
      </ButtonColumnContainer>
      <SpreadApartArea>
        <Button buttonType="inverted" onClick={() => onFinished(false)}>
          Cancel
        </Button>
        <Button onClick={submitHandler}>Save</Button>
      </SpreadApartArea>
    </>
  );
};

export default UgisPartnerForm;
